import axios from '@/lib/axios'
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/vue-query'
import { update } from 'lodash'
import { ToastInterface, useToast } from 'vue-toastification'

import { Email } from '@/interfaces/email'
import { Phone } from '@/interfaces/phone'

interface Params extends Omit<Partial<Email>, 'id'> {
	emailId?: string | number
	countryCodeId?: string | number
	showToasts?: boolean
}

interface Props {
	immediate?: boolean
	params?: Params
}

type UpdateParams = Omit<Params, 'id'>

const getUserEmail = async (params?: Params): Promise<Phone | void> => {
	if (params) {
		const { emailId } = params
		const { data } = await axios.get(`/api/emails/${emailId}`)

		return data
	}
}

const updateEmail = async (
	params: UpdateParams,
	toast: ToastInterface
): Promise<Email | undefined> => {
	try {
		const { emailId, showToasts = true } = params
		const { data } = await axios.put(`/api/emails/${emailId}`, params)

		if (showToasts) {
			toast.success('Correo actualizado con éxito')
		}
		return data
	} catch (err: any) {
		toast.error(err.response.data.message)
		return undefined
	}
}

const deleteEmail = async (
	params: Required<Pick<Params, 'emailId'>>,
	toast: ToastInterface,
	queryClient: QueryClient
): Promise<Email | undefined> => {
	try {
		const { emailId } = params
		const { data } = await axios.delete(`/api/emails/${emailId}`)

		queryClient.invalidateQueries(['email', emailId])
		toast.success('Correo eliminado con éxito')
		return data
	} catch (err: any) {
		toast.error(err.response.data.message)
	}
}

const useEmail = (props: Props) => {
	const toast = useToast()
	const queryClient = useQueryClient()

	const { immediate = true, params } = props || {}

	const { data, isLoading } = useQuery(
		['phone', params?.emailId],
		() => getUserEmail(params),
		{
			enabled: immediate,
			refetchOnWindowFocus: true,
			retry: false,
		}
	)

	const updateMutation = useMutation((params: UpdateParams) =>
		updateEmail(params, toast)
	)

	const deleteMutation = useMutation(
		(params: Required<Pick<Params, 'emailId'>>) =>
			deleteEmail(params, toast, queryClient)
	)

	return {
		phone: data,
		isLoading,

		updateEmailMutation: updateMutation,
		updateEmail: updateMutation.mutate,
		updateEmailAsync: updateMutation.mutateAsync,
		updateEmailLoading: updateMutation.isLoading,
		updateEmailError: updateMutation.error,

		deleteEmailMutation: deleteMutation,
		deleteEmail: deleteMutation.mutate,
		deleteEmailAsync: deleteMutation.mutateAsync,
	}
}

export default useEmail
